import React, { useEffect } from 'react';
import { Modal, Steps } from 'antd';
import styled from 'styled-components';
import UploadStep from './UploadStep';
import MappingStep from './MappingStep';
import EnrichmentStep from './EnrichmentStep';
import SummaryStep from './SummaryStep';
import { useSelector, useDispatch } from 'react-redux';
import {
  incrementEnrichmentWizardStep,
  decrementEnrichmentWizardStep,
  resetEnrichmentWizardState,
} from 'actions/enrichmentWizard';
import {
  getEnrichmentData,
  getEnrichmentMappings,
  getEnrichmentWizardCurrentStep,
  getEnrichmentStatus,
  getEnrichmentFile,
} from 'selectors/enrichmentWizard';
import { getRequiredFields } from './validationConfig';
import { useReadFile } from './Hooks/useReadFile';
import usePostEnrich from './Hooks/usePostEnrich';
import { Button } from 'components/common';

const WizardContainer = styled.div`
  min-height: 550px;
  display: flex;
  flex-direction: column;
`;

const StepContent = styled.div`
  flex: 1;
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const BackForthButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const CancelButton = styled(Button)`
  margin-right: auto;
`;

const WizardSteps = styled(Steps)`
  padding: 0 16px;
  .ant-steps-item-icon {
    line-height: 28px;
  }
`;

export const stepNumbers = {
  upload: 0,
  mapping: 1,
  enrichment: 2,
  summary: 3,
};

const steps = [
  {
    title: 'Upload',
    description: type =>
      type === 'email'
        ? 'Upload File for Email Enrichment'
        : 'Upload File for Organisation Enrichment',
    component: UploadStep,
    stepNumber: stepNumbers.upload,
  },
  {
    title: 'Mapping',
    description: type =>
      type === 'email'
        ? 'Map Email Fields to Data Categories'
        : 'Map Organisation Fields to Data Categories',
    component: MappingStep,
    stepNumber: stepNumbers.mapping,
  },
  {
    title: 'Enrichment',
    description: () => 'Enrich Your Data',
    component: EnrichmentStep,
    buttonText: 'Enrich My File',
    stepNumber: stepNumbers.enrichment,
  },
  {
    title: 'Summary',
    description: () => 'Your Data is Ready, View Enrichment List',
    component: SummaryStep,
    stepNumber: stepNumbers.summary,
  },
];

/**
 * Wizard component for data enrichment process
 * @param {Object} props Component props
 * @param {Function} props.onClose Callback when modal is closed
 * @param {'email' | 'organisation'} props.type The type of enrichment to perform
 * @param {string} props.summaryId The ID of the summary to use
 * @param {boolean} props.isProcessing Whether the enrichment is processing
 * @returns {JSX.Element} Wizard modal component
 */
const Wizard = ({
  onClose,
  type = 'email',
  summaryId,
  isProcessing = false,
}) => {
  const dispatch = useDispatch();
  const data = useSelector(getEnrichmentData);
  const mappings = useSelector(getEnrichmentMappings);
  const currentStep = useSelector(getEnrichmentWizardCurrentStep);
  const enrichmentStatus = useSelector(getEnrichmentStatus);
  const file = useSelector(getEnrichmentFile);

  useReadFile(file);
  const [
    { res: resEnrichment, loading: isEnriching },
    handleStartEnrichment,
  ] = usePostEnrich();

  useEffect(() => {
    if (resEnrichment) {
      dispatch(incrementEnrichmentWizardStep());
    }
  }, [resEnrichment, dispatch]);

  useEffect(() => {
    dispatch(resetEnrichmentWizardState(type, summaryId, isProcessing));
    return () => {
      dispatch(resetEnrichmentWizardState());
    };
  }, [dispatch, type, summaryId, isProcessing]);

  const handleNext = () => {
    if (currentStep + 1 === stepNumbers.enrichment) {
      handleStartEnrichment();
    } else {
      dispatch(incrementEnrichmentWizardStep());
    }
  };

  const handlePrev = () => {
    dispatch(decrementEnrichmentWizardStep());
  };

  const handleClose = (confirmed = false, options = {}) => {
    if (!data?.length) {
      confirmed = true;
    }

    if (currentStep === stepNumbers.enrichment) {
      options = { ...options, reload: true };
    }

    if (currentStep < stepNumbers.enrichment) {
      const confirmClose =
        confirmed ||
        window.confirm(
          "Are you sure you want to close the upload process? Any progress you've made will be lost."
        );
      if (confirmClose) {
        onClose(options);
        dispatch(resetEnrichmentWizardState());
      }
    } else {
      onClose(options);
      dispatch(resetEnrichmentWizardState());
    }
  };

  const finishWizard = () => {
    handleClose(true, { reload: !summaryId });
  };

  const isNextDisabled = () => {
    if (currentStep === stepNumbers.upload) {
      return !data?.length;
    }
    if (currentStep === stepNumbers.mapping) {
      const requiredMappings = getRequiredFields(type);
      return requiredMappings.some(
        mappingId =>
          !mappings[mappingId] ||
          mappings[mappingId].column == null ||
          !mappings[mappingId].validation.valid.length
      );
    }
    if (currentStep === stepNumbers.enrichment) {
      return enrichmentStatus !== 'complete';
    }
    return false;
  };

  const isBackDisabled = () => {
    if (currentStep === stepNumbers.enrichment) {
      return enrichmentStatus !== null;
    }
    if (currentStep === stepNumbers.summary) {
      return true;
    }
    return false;
  };

  const CurrentStepComponent = steps[currentStep].component;

  useEffect(() => {
    if (
      currentStep > 0 &&
      currentStep < stepNumbers.enrichment &&
      !data?.length
    ) {
      dispatch(decrementEnrichmentWizardStep());
    }
  }, [data, currentStep, dispatch]);

  return (
    <Modal
      open
      title={steps[currentStep].description(type)}
      onCancel={() => handleClose(false)}
      width={800}
      footer={
        <ButtonContainer>
          {currentStep === stepNumbers.summary ? (
            <div></div>
          ) : (
            <CancelButton onClick={() => handleClose(false)}>
              Cancel
            </CancelButton>
          )}
          <BackForthButtonContainer>
            <div>
              {summaryId ? (
                <Button key='close' onClick={() => handleClose(true)}>
                  Close
                </Button>
              ) : (
                currentStep > 0 &&
                !isBackDisabled() && (
                  <Button
                    key='back'
                    onClick={handlePrev}
                    disabled={isBackDisabled()}
                  >
                    Previous
                  </Button>
                )
              )}
            </div>
            <div>
              {currentStep < steps.length - 1 ? (
                <Button
                  key='next'
                  type='primary'
                  onClick={handleNext}
                  disabled={isNextDisabled() || isEnriching}
                >
                  {steps[currentStep + 1].buttonText || 'Next'}
                </Button>
              ) : (
                !summaryId && (
                  <Button key='finish' type='primary' onClick={finishWizard}>
                    Finish
                  </Button>
                )
              )}
            </div>
          </BackForthButtonContainer>
        </ButtonContainer>
      }
    >
      <WizardContainer>
        <>
          <WizardSteps
            current={currentStep}
            items={steps.map(({ title }) => ({
              title,
            }))}
          />
          <StepContent>
            <CurrentStepComponent />
          </StepContent>
        </>
      </WizardContainer>
    </Modal>
  );
};

export default Wizard;
